'use client';

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Link from 'next/link';
import styles from '../styles/WalletConnect.module.css';
import { ClientPurchaseModal } from './ClientPurchaseModal';

gsap.registerPlugin(ScrollTrigger); // Register plugin outside component

const WalletConnect = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const stepsRef = useRef([]);
  const buttonRef = useRef(null);
  const helpRef = useRef(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  useEffect(() => {
    // Create gsap context for scoping and automatic cleanup
    const ctx = gsap.context(() => {
      // Title animation
      if (titleRef.current) {
        gsap.fromTo(
          titleRef.current,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            scrollTrigger: {
              trigger: titleRef.current,
              start: 'top 80%',
              end: 'bottom 20%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }

      // Steps animations with stagger effect
      if (stepsRef.current.length) {
        gsap.fromTo(
          stepsRef.current,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.2,
            scrollTrigger: {
              trigger: stepsRef.current[0],
              start: 'top 80%',
              end: 'bottom 20%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }

      // Help section animation
      if (helpRef.current) {
        gsap.fromTo(
          helpRef.current,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            scrollTrigger: {
              trigger: helpRef.current,
              start: 'top 85%',
              end: 'bottom 20%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }

      // Button animation
      if (buttonRef.current) {
        gsap.fromTo(
          buttonRef.current,
          { opacity: 0, scale: 0.9 },
          {
            opacity: 1,
            scale: 1,
            duration: 0.8,
            delay: 0.3,
            scrollTrigger: {
              trigger: buttonRef.current,
              start: 'top 90%',
              end: 'bottom 20%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    }, sectionRef); // Scope the context to the section element
    
    // Return ctx.revert() for automatic cleanup
    return () => ctx.revert();
  }, []);

  // Steps data
  const steps = [
    {
      number: 1,
      title: "Connect Your Wallet",
      content: "Connect your wallet to our platform to begin the token purchase process securely."
    },
    {
      number: 2,
      title: "Select Network",
      content: "After connecting your wallet, choose either Base or Solana network as we accept ETH, USDT, USDC, BNB, or SOL."
    },
    {
      number: 3,
      title: "Purchase Tokens",
      content: "Input the amount of GAM tokens you intend to buy with ETH, USDT, USDC, BNB, or SOL before confirming the transaction."
    },
    {
      number: 4,
      title: "Token Claim",
      content: "At our TGE, you can simply claim your tokens on our website using the same wallet and network."
    }
  ];

  return (
    <section className={styles.walletConnect} id="wallet-connect" ref={sectionRef}>
      <div className={styles.walletConnectHeader}>
        <h2 className={styles.walletConnectTitle} ref={titleRef}>Connect Your Wallet</h2>
      </div>

      <div className={styles.stepsContainer}>
        {steps.map((step, index) => (
          <div 
            key={index} 
            className={styles.step}
            ref={el => stepsRef.current[index] = el}
          >
            <div className={styles.stepNumber}>{step.number}</div>
            <h3 className={styles.stepTitle}>{step.title}</h3>
            <p className={styles.stepContent}>{step.content}</p>
          </div>
        ))}
      </div>

      <div className={styles.helpContainer} ref={helpRef}>
        <p className={styles.helpText}>
          Need Help? Check our <Link href="/payment-info" className={styles.tutorialLink}>payment tutorial</Link>
        </p>
      </div>

      <ClientPurchaseModal 
        isOpen={showPurchaseModal} 
        onClose={() => setShowPurchaseModal(false)} 
      />
    </section>
  );
};

export default WalletConnect; 